import { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, usePaginatedQuery, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import {
  mapActivityHours,
  mapEventsResult,
  mapMonitorsResult,
  formatActivityHourLabel,
} from "../api/profile";
import { AlertTriangle, ChevronLeft, Trash2 } from "lucide-react";
import { TimelineEventCard } from "../components/TimelineEventCard";
import { ProfileStatusBadge } from "../components/ProfileStatusBadge";
import { EmptyState } from "../components/ui/EmptyState";
import { LoadingState } from "../components/ui/LoadingState";
import { getReliabilityViewModel } from "../domain/reliability";
import type { MonitoringEvent, TimelineEventViewModel } from "../domain/types";
import { t } from "../i18n";
import { formatRelativeTime } from "../lib/time";
import {
  ProfileDossierModal,
  type DossierProfile,
} from "../components/ProfileDossierModal";

function toTimelineEvent(e: MonitoringEvent): TimelineEventViewModel {
  const typeMap: Record<string, TimelineEventViewModel["type"]> = {
    STARTED_FOLLOWING: "started_following",
    STOPPED_FOLLOWING: "stopped_following",
    GAINED_FOLLOWER: "gained_follower",
    LOST_FOLLOWER: "lost_follower",
  };
  const confMap: Record<string, TimelineEventViewModel["confidence"]> = {
    high: "confirmed",
    medium: "confirmed",
    low: "low_confidence",
  };

  return {
    id: e.id,
    type: typeMap[e.action] ?? "started_following",
    confidence: confMap[e.confidence] ?? "pending_confirmation",
    occurredAt: e.occurredAt,
    targetProfile: {
      handle: e.targetHandle,
      displayName: e.targetDisplayName,
      avatarUrl: e.targetAvatarUrl,
      gender: e.gender,
      isVerified: e.isVerified,
      isMutual: e.isMutual,
      category: e.category,
      followerCount: e.followerCount,
      followingCount: e.followingCount,
      biography: e.biography,
      isPrivate: e.isPrivate,
    },
    sourceProfile: { handle: "" },
    description: "",
  };
}

export function AppProfileDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"follows" | "unfollows">(
    "follows",
  );
  const [selectedDossier, setSelectedDossier] = useState<DossierProfile | null>(
    null,
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const rawProfiles = useQuery(api.monitors.list, {});
  const profiles = useMemo(() => mapMonitorsResult(rawProfiles), [rawProfiles]);
  const profile = profiles.find((p) => p.id === id) ?? null;
  const monitoredAccountId = profile?.monitoredAccountId as
    | Id<"monitoredAccounts">
    | undefined;
  const retentionStart = useMemo(
    () => Date.now() - 90 * 24 * 60 * 60 * 1000,
    [],
  );
  const followsPagination = usePaginatedQuery(
    api.events.getPaginatedEvents,
    monitoredAccountId
      ? {
          monitoredAccountId,
          retentionStart,
          actionFilter: "STARTED_FOLLOWING" as const,
        }
      : "skip",
    { initialNumItems: 20 },
  );
  const unfollowsPagination = usePaginatedQuery(
    api.events.getPaginatedEvents,
    monitoredAccountId
      ? {
          monitoredAccountId,
          retentionStart,
          actionFilter: "STOPPED_FOLLOWING" as const,
        }
      : "skip",
    { initialNumItems: 20 },
  );
  const rawActivityHours = useQuery(
    api.engagement.getActivityHours,
    monitoredAccountId ? { monitoredAccountId } : "skip",
  );
  const removeMonitor = useMutation(api.monitors.remove);
  const activePagination =
    activeTab === "follows" ? followsPagination : unfollowsPagination;
  const events = useMemo(
    () => mapEventsResult(activePagination.results),
    [activePagination.results],
  );
  const activityHours = useMemo(
    () => mapActivityHours(rawActivityHours),
    [rawActivityHours],
  );
  const isLoading =
    rawProfiles === undefined ||
    (monitoredAccountId !== undefined &&
      (rawActivityHours === undefined ||
        activePagination.status === "LoadingFirstPage"));

  if (isLoading) return <LoadingState title={t("commonLoading")} />;
  if (!profile) {
    return (
      <EmptyState
        title={t("previewNotFound")}
        body={t("profileDetailRemoved")}
        action={{ label: t("commonBack"), onClick: () => navigate("/app") }}
      />
    );
  }

  const reliability = getReliabilityViewModel(
    profile.status === "PRIVATE"
      ? "private_profile"
      : profile.status === "RATE_LIMITED"
        ? "degraded"
        : profile.status === "DISABLED"
          ? "paused_unreliable"
          : profile.monitoringMode === "PAUSED_UNRELIABLE"
            ? "paused_unreliable"
            : "reliable",
  );

  const visibleEvents = events;

  return (
    <div className="app-page">
      <button onClick={() => navigate(-1)} className="app-back-button">
        <ChevronLeft size={34} />
        <span>{t("commonBack")}</span>
      </button>

      <section className="app-detail-hero">
        <div className="app-detail-identity">
          <div className="app-avatar app-avatar-lg">
            {profile.avatarUrl ? (
              <img src={profile.avatarUrl} alt="" />
            ) : (
              (profile.displayName?.charAt(0) ??
              profile.handle.charAt(0).toUpperCase())
            )}
          </div>
          <div className="app-detail-info">
            <div className="app-detail-title-row">
              <h1>@{profile.handle}</h1>
              <ProfileStatusBadge status={reliability.status} />
            </div>
            {profile.lastScrapedAt && (
              <small className="app-detail-updated">
                {t("profileUpdatedAt", {
                  time: formatRelativeTime(profile.lastScrapedAt),
                })}
              </small>
            )}
          </div>
        </div>

        <div className="app-detail-stats-row">
          <div className="app-detail-stat-col">
            <span className="app-detail-stat-num">
              {profile.followingCount.toLocaleString()}
            </span>
            <span className="app-detail-stat-label">
              {t("dossierFollowing")}
            </span>
          </div>
          <div className="app-detail-stat-col">
            <span className="app-detail-stat-num app-delta-positive">
              +{profile.newFollowsCount ?? 0}
            </span>
            <span className="app-detail-stat-label">{t("statNew")}</span>
          </div>
          <div className="app-detail-stat-col">
            <span className="app-detail-stat-num app-delta-negative">
              -{profile.unfollowsCount ?? 0}
            </span>
            <span className="app-detail-stat-label">{t("tabUnfollows")}</span>
          </div>
        </div>
      </section>

      {profile.status === "PRIVATE" && (
        <section className="app-private-warning">
          <AlertTriangle size={20} />
          <p>{t("monitorDetailsPrivateWaiting")}</p>
        </section>
      )}

      {!showDeleteConfirm ? (
        <button
          onClick={() => {
            setDeleteError(null);
            setShowDeleteConfirm(true);
          }}
          className="app-danger-button app-delete-monitor-button"
        >
          <Trash2 size={23} />
          {t("profileDetailRemoveBtn")}
        </button>
      ) : (
        <section className="app-danger-confirm app-monitor-delete-confirm">
          <p>{t("monitorDetailsDeleteDescription")}</p>
          {deleteError && <p>{deleteError}</p>}
          <div>
            <button
              type="button"
              className="app-danger-button"
              disabled={isRemoving}
              onClick={async () => {
                if (isRemoving) return;
                setIsRemoving(true);
                setDeleteError(null);
                try {
                  await removeMonitor({
                    monitorId: profile.id as Id<"userMonitors">,
                  });
                  navigate("/app");
                } catch {
                  setDeleteError(t("monitorDetailsDeleteError"));
                  setIsRemoving(false);
                }
              }}
            >
              {isRemoving
                ? t("commonLoading")
                : t("monitorDetailsDeleteConfirm")}
            </button>
            <button
              type="button"
              className="app-secondary-button"
              disabled={isRemoving}
              onClick={() => setShowDeleteConfirm(false)}
            >
              {t("accountCancel")}
            </button>
          </div>
        </section>
      )}

      {activityHours && activityHours.totalWithHour > 0 && (
        <section className="app-heatmap-card">
          <div className="app-section-heading">
            <h2>{t("activityHoursTitle")}</h2>
          </div>
          <div className="app-heatmap-grid">
            {activityHours.blocks.map((block) => {
              const max = Math.max(
                ...activityHours.blocks.map((b) => b.count),
                1,
              );
              const intensity =
                block.count === 0 ? 0 : Math.max(0.18, block.count / max);
              return (
                <div key={block.label} className="app-heatmap-block">
                  <span style={{ opacity: 0.35 + intensity * 0.65 }}>
                    {block.count}
                  </span>
                  <small>{formatActivityHourLabel(block.label)}</small>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="app-section-heading app-activity-heading">
        <h2>{t("monitorDetailsRecentActivity")}</h2>
      </div>

      <div className="app-segmented">
        <button
          className={activeTab === "follows" ? "is-active" : ""}
          onClick={() => setActiveTab("follows")}
        >
          {t("tabFollows")}
        </button>
        <button
          className={activeTab === "unfollows" ? "is-active" : ""}
          onClick={() => setActiveTab("unfollows")}
        >
          {t("tabUnfollows")}
        </button>
      </div>

      {visibleEvents.length === 0 ? (
        <EmptyState
          title={
            activeTab === "follows"
              ? t("monitorDetailsMonitoringStarted")
              : t("emptyUnfollows")
          }
          body={
            activeTab === "follows"
              ? t("monitorDetailsMonitoringStartedHint")
              : t("timelineNoEventsHint")
          }
        />
      ) : (
        <>
          <div className="app-timeline-list">
            {visibleEvents.map((e) => (
              <TimelineEventCard
                key={e.id}
                event={toTimelineEvent(e)}
                onClick={() => {
                  setSelectedDossier({
                    handle: e.targetHandle,
                    fullName: e.targetDisplayName,
                    avatarUrl: e.targetAvatarUrl,
                    isVerified: e.isVerified,
                    isMutual: e.isMutual,
                    followerCount: e.followerCount,
                    followingCount: e.followingCount,
                    category: e.category,
                    isPrivate: e.isPrivate,
                    biography: e.biography,
                    gender: e.gender,
                  });
                }}
              />
            ))}
          </div>
          {activePagination.status === "CanLoadMore" && (
            <button
              type="button"
              className="app-secondary-button app-load-more-button"
              onClick={() => activePagination.loadMore(20)}
            >
              {t("commonLoadMore")}
            </button>
          )}
        </>
      )}

      <p className="app-caption">{t("timelineEventsRetention")}</p>

      <ProfileDossierModal
        profile={selectedDossier}
        onClose={() => setSelectedDossier(null)}
      />
    </div>
  );
}
